import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { Route } from '@metro/routing'
import { RouteResultsList, type LineDisplay } from '@metro/route-card'
import {
  buildRouteDetailsSections,
  RoutesCollectionRow,
  MapRow,
  RouteDetailsTestTags,
  ValidationRow,
  EntryRow,
  FirstStopRow,
  ExpandRow,
  MiddleStopRow,
  LastStopRow,
  TransferRow,
  StreetWalkRow,
  StreetEndpointRow,
  ReportIssueRow,
  type RouteDetailsSection,
  type StationResolver,
  type ConvenientCarType,
  type RouteDetailsAnalyticsReporter,
  type RouteDetailsStringSet,
} from '@metro/route-details'
import { realRouteDetailsAnalytics } from '../analytics/webAnalyticsReporter'
import { buildRouteDetailsStrings, buildRouteCardCopy } from '../i18n/packageStrings'
import { useSwipeDownDismiss } from './useSwipeDownDismiss'
import styles from './MobileRouteDetailsOverlay.module.css'

/**
 * RouteDetails — full-height modal dialog (phone, states S1–S5).
 *
 * Stateless-composition port of the iOS `RouteDetailsView` /
 * `RouteDetailsOptionView` + reducer behaviour. The host (the
 * RouteCard overlay) owns the route data + premium flag and passes
 * them in; this component owns only the per-line expand state + the
 * once-fired upsell-shown latch (the rest is callbacks out).
 *
 * Layout: chips pinned at the top, then the fabric sections in Figma
 * order (QA spec §1.3 / §11-Q1), then a separator + Report-issue row.
 * Dismiss is backdrop-click + Escape only — no in-content close button
 * (§11-Q4). Analytics names + params are the verbatim cross-platform
 * contract (§5): select on chip tap, expand on chevron tap,
 * upsell-shown once when the banner first becomes visible, upsell-tapped
 * on banner tap.
 */
export interface MobileRouteDetailsOverlayProps {
  /** All route variants for the chip row. */
  readonly rawRoutes: readonly Route[]
  /** Currently-selected route (drives the section list). */
  readonly selectedRoute: Route
  /** Premium gates the S4 car-strip vs the S5 upsell banner. */
  readonly isPremium: boolean
  /** line_id → display info (reused from the RouteCard adapter). */
  readonly lineLookup: ReadonlyMap<string, LineDisplay>
  /** Resolves a stationId to its display name (host schema lookup). */
  readonly resolveStation: StationResolver
  /** Convenient-transfer cars per ride-leg index (drives S4/S5). */
  readonly possibleTransfersByLeg?: Readonly<Record<number, readonly ConvenientCarType[]>>
  /** Platform sign ("Westbound", …) per ride-leg index (the first-stop chip). */
  readonly platformBoundsByLeg?: Readonly<Record<number, string>>
  /** Ride-leg indices that render outlined (DLR / Overground / Trams). */
  readonly outlinedLegs?: ReadonlySet<number>
  /** Dismiss the modal (backdrop / Escape). */
  readonly onClose: () => void
  /** A chip was tapped — host swaps the selected route. */
  readonly onSelectRoute: (route: Route) => void
  /** Upsell banner tapped — host opens the transfer paywall. */
  readonly onOpenTransferPaywall: () => void
  /** Report-issue row tapped — host opens the feedback flow. */
  readonly onReportIssue: () => void
  /**
   * Analytics sink. Defaults to `realRouteDetailsAnalytics` so the
   * RouteDetails §5 events forward to GA4 + Amplitude
   * in production (no-op outside production / before consent). Tests
   * inject a spy.
   */
  readonly onAnalytics?: RouteDetailsAnalyticsReporter
  /** Test/host seam for the arrival-time clock. */
  readonly clock?: () => Date
  /**
   * Presentation chrome (TASK-412). `overlay` (default) is the phone
   * full-screen modal: a fixed `.backdrop` + `.sheet`, backdrop-click
   * dismiss, `aria-modal`. `sidePanel` is the tablet/desktop layout: the
   * host card (`.expandedSidebar`) supplies the chrome, so the overlay
   * drops the backdrop + sheet and renders the sections bare (content
   * only), with no backdrop dismiss — mirrors StationDetails's
   * content/chrome split and the iOS RouteDetails `.sidePanel` variant.
   * Escape still dismisses on both.
   */
  readonly layout?: 'overlay' | 'sidePanel'
}

/**
 * `station_from_id` — first stop of the FIRST ride leg (mirrors iOS
 * `RouteDetailsAnalytics.fromStationId`: first stop of the first line
 * section). `undefined` when the route has no ride leg / no stops.
 */
function fromStationId(route: Route): string | undefined {
  for (const leg of route.legs) {
    if (leg.type === 'ride') return leg.stops?.[0]?.stationId
  }
  return undefined
}

/**
 * `station_to_id` — last stop of the LAST ride leg (mirrors iOS
 * `RouteDetailsAnalytics.toStationId`: last stop of the last line
 * section). `undefined` when the route has no ride leg / no stops.
 */
function toStationId(route: Route): string | undefined {
  for (let i = route.legs.length - 1; i >= 0; i--) {
    const leg = route.legs[i]
    if (leg !== undefined && leg.type === 'ride') {
      const stops = leg.stops
      return stops?.[stops.length - 1]?.stationId
    }
  }
  return undefined
}

/**
 * Route-identity params shared by every RouteDetails analytics event.
 * `station_from_id` / `station_to_id` are derived from the resolved
 * route's first/last ride-leg stops (byte-identical with iOS / Android);
 * they are `undefined` (→ key omitted by the core sink, parity with the
 * other platforms' omit-when-null) only when the route has no ride leg.
 */
function routeParams(route: Route): Record<string, unknown> {
  return {
    route_id: route.id,
    station_to_id: toStationId(route),
    station_from_id: fromStationId(route),
  }
}

/**
 * `true` when the selected route would show the upsell banner: a
 * non-premium user on a route that has a line with a transfer after it
 * AND convenient cars (mirrors iOS `_shouldShowUpsellBanner`).
 */
function shouldShowUpsell(
  route: Route,
  isPremium: boolean,
  possibleTransfersByLeg: Readonly<Record<number, readonly ConvenientCarType[]>>,
): boolean {
  if (isPremium) return false
  return route.legs.some((leg, index) => {
    if (leg.type !== 'ride') return false
    // Interchange = the next leg is a transfer OR a walk (folded; mirrors
    // the fabric's `foldedTransferAfter`).
    const next = route.legs[index + 1]
    const hasInterchangeAfter =
      next !== undefined && (next.type === 'transfer' || next.type === 'walk')
    const cars = possibleTransfersByLeg[index]
    return hasInterchangeAfter && cars !== undefined && cars.length > 0
  })
}

export function MobileRouteDetailsOverlay(props: MobileRouteDetailsOverlayProps): ReactElement {
  const {
    rawRoutes,
    selectedRoute,
    isPremium,
    lineLookup,
    resolveStation,
    possibleTransfersByLeg = {},
    platformBoundsByLeg,
    outlinedLegs,
    onClose,
    onSelectRoute,
    onOpenTransferPaywall,
    onReportIssue,
    onAnalytics = realRouteDetailsAnalytics,
    clock,
    layout = 'overlay',
  } = props
  const isSidePanel = layout === 'sidePanel'
  const { t } = useTranslation('route-details')
  const { t: tRouteCard } = useTranslation('route-card')
  // Localised label set threaded into the pure section builder (which
  // cannot read i18next itself). en values are byte-identical to the
  // English defaults, so the en render is unchanged.
  const strings = useMemo(() => buildRouteDetailsStrings(t), [t])
  // The side-panel variant selector reuses @metro/route-card's
  // RouteResultsList; localise its results-list aria from the host.
  const routeCardCopy = useMemo(() => buildRouteCardCopy(tRouteCard), [tRouteCard])

  // Per-ride-leg expand state, keyed by leg index. Default collapsed
  // (matches iOS `prepareLocalStorages` which seeds every line index to
  // `false`). Reset when the selected route changes.
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})
  // Reset-on-prop-change is adjusted DURING render rather than in an effect.
  // An effect would commit one render carrying the previous route's expand
  // state, then immediately re-render — the user can see a leg flash open on
  // the newly selected route. Adjusting here means React re-runs this component
  // before touching the DOM, so nothing stale is ever painted. This is the
  // pattern React documents for "reset state when a prop changes"; it is also
  // why `react-hooks/set-state-in-effect` flags the effect form.
  const [prevRouteId, setPrevRouteId] = useState(selectedRoute.id)
  if (prevRouteId !== selectedRoute.id) {
    setPrevRouteId(selectedRoute.id)
    setExpanded({})
  }

  // Escape dismiss (mirrors StationDetailsModal).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return (): void => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  // Swipe-down-to-dismiss on the phone modal (Bug 4 — parity with
  // StationDetailsModal, which uses the SAME hook / threshold / animation).
  // The gesture is bound to the drag-handle zone; the sheet itself trails
  // the pointer via `sheetStyle`. Unused by the sidePanel layout (no sheet).
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const { springing, sheetStyle, handlers: dragHandlers } = useSwipeDownDismiss(sheetRef, onClose)

  const sections = useMemo(
    () =>
      buildRouteDetailsSections({
        route: selectedRoute,
        lineLookup,
        resolveStation,
        isPremium,
        expanded,
        clock,
        possibleTransfersByLeg,
        platformBoundsByLeg,
        outlinedLegs,
        strings,
      }),
    [
      selectedRoute,
      lineLookup,
      resolveStation,
      isPremium,
      expanded,
      clock,
      possibleTransfersByLeg,
      platformBoundsByLeg,
      outlinedLegs,
      strings,
    ],
  )

  // Fire `route_details_upsell_banner_shown` ONCE per qualifying show —
  // on first mount when the gate is true, and on a route switch that
  // newly satisfies it; never on a plain re-render / expand toggle
  // (QA spec §5 fire-once rule). The latch is keyed by route id.
  const shownForRouteRef = useRef<string | null>(null)
  useEffect(() => {
    const qualifies = shouldShowUpsell(selectedRoute, isPremium, possibleTransfersByLeg)
    if (qualifies && shownForRouteRef.current !== selectedRoute.id) {
      shownForRouteRef.current = selectedRoute.id
      onAnalytics('route_details_upsell_banner_shown', routeParams(selectedRoute))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on route id + premium; possibleTransfersByLeg is stable per route.
  }, [selectedRoute.id, isPremium])

  const handleSelectRoute = useCallback(
    (route: Route) => {
      if (route.id === selectedRoute.id) return
      onAnalytics('route_details_select_route_tapped', routeParams(route))
      onSelectRoute(route)
    },
    [selectedRoute.id, onAnalytics, onSelectRoute],
  )

  const handleExpand = useCallback(
    (legIndex: number) => {
      setExpanded((prev) => ({ ...prev, [legIndex]: !(prev[legIndex] ?? false) }))
      onAnalytics('route_details_expand_row_tapped', {
        index: legIndex,
        ...routeParams(selectedRoute),
      })
    },
    [onAnalytics, selectedRoute],
  )

  // TODO(paywall-web): premium features are FREE on web until the web paywall lands.
  // iOS + Android route this to Adapty placement `com.mmthstudio.possible-transfer.v1`
  // (or to the stub-paywall mock when `metro_paywall_stub=yes`). On web there is no
  // paywall mount, so this tap emits analytics only — see QA scenarios doc §5.2.
  const handleUpsellTap = useCallback(() => {
    onAnalytics('route_details_upsell_banner_tapped', routeParams(selectedRoute))
    onOpenTransferPaywall()
  }, [onAnalytics, onOpenTransferPaywall, selectedRoute])

  // TASK-898: the route-details entry into the feedback funnel, reported with
  // the same identity params as the other route_details_* events so the
  // funnel can join it to feedback_submitted / feedback_dismissed.
  const handleReportIssueTap = useCallback(() => {
    onAnalytics('route_details_report_tapped', routeParams(selectedRoute))
    onReportIssue()
  }, [onAnalytics, onReportIssue, selectedRoute])

  const onBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  // Variant selector at the top of the detail. TASK-412 (D mirror —
  // user-confirmed iOS look @b23465455): the tablet/desktop side panel
  // shows a LARGE selector — the same vertical `RouteResultsList`
  // (variant="tablet") full-width cards the RouteCard results state
  // used to render, now owned by RouteDetails so the panel has a single
  // selector (the RouteCard results card is dropped on tablet). The
  // phone overlay is UNCHANGED: it keeps the small horizontal
  // `RoutesCollectionRow` chip strip. Both drive the same
  // `handleSelectRoute` (selection + analytics); the large selector is
  // index-based (RouteResultsList API) so we bridge index → route.
  const selectedIndex = rawRoutes.findIndex((r) => r.id === selectedRoute.id)
  const variantSelector = isSidePanel ? (
    <RouteResultsList
      routes={rawRoutes}
      selectedIndex={selectedIndex < 0 ? 0 : selectedIndex}
      lineLookup={lineLookup}
      onSelect={(index) => {
        const route = rawRoutes[index]
        if (route !== undefined) handleSelectRoute(route)
      }}
      clock={clock}
      variant="tablet"
      resultsListAria={routeCardCopy.resultsListAria}
    />
  ) : (
    <RoutesCollectionRow
      routes={rawRoutes}
      selectedRouteId={selectedRoute.id}
      lineLookup={lineLookup}
      onSelect={handleSelectRoute}
      clock={clock}
    />
  )

  const body = (
    <div className={styles.body}>
      {variantSelector}
      {sections.map((section, index) =>
        renderSection(section, index, handleExpand, handleUpsellTap, strings),
      )}
      <div className={styles.separator} />
      <ReportIssueRow onTap={handleReportIssueTap} strings={strings} />
    </div>
  )

  // sidePanel — the host card (.expandedSidebar) is the chrome. Render
  // the content bare in a flex column that fills the host slot (mirrors
  // StationDetails `.root`); no fixed backdrop/sheet, no backdrop
  // dismiss, no aria-modal (it is an inline panel region, not a modal).
  // Escape still dismisses (wired above).
  if (isSidePanel) {
    return (
      <div
        className={styles.sidePanel}
        data-testid={RouteDetailsTestTags.root}
        role="dialog"
        aria-label={t('route_details_aria_panel', { defaultValue: 'Route details' })}
        // TASK-412 R was: transparent cutouts for the TRANSLUCENT glass panel.
        // 2026-07-06: the side panel is now a ~opaque (95%) surface, so
        // transparent cutouts made the first/last ring holes and the middle-
        // stop notches invisible (the coloured line showed straight through
        // the "gap"). Leave `--rd-cutout-color` unset so it defaults to the
        // opaque `--mm-color-background-primary` — same as the phone modal —
        // and the ring holes + notches read as clean gaps again.
      >
        {body}
      </div>
    )
  }

  return (
    <div
      className={styles.backdrop}
      data-testid={RouteDetailsTestTags.root}
      role="dialog"
      aria-modal="true"
      aria-label={t('route_details_aria_panel', { defaultValue: 'Route details' })}
      onClick={onBackdropClick}
    >
      <div
        ref={sheetRef}
        className={`${styles.sheet} ${springing ? styles.springing : ''}`}
        style={sheetStyle}
        data-testid="route-details-sheet"
      >
        {/*
         * Drag-handle pill — always visible in the phone overlay layout
         * (parity with StationDetailsModal). Mirrors iOS
         * `.presentationDragIndicator(.visible)` and Android
         * `ModalBottomSheet` drag handle as the discoverable dismiss
         * affordance for mobile-web touch (where Escape is unavailable).
         * Tap closes; swipe-down past 30% (or a fast flick) dismisses via
         * `useSwipeDownDismiss` — parity with StationDetailsModal (Bug 4).
         */}
        <button
          type="button"
          className={styles.dragHandle}
          data-testid="route-details-drag-handle"
          aria-label={t('route_details_aria_close', { defaultValue: 'Close route details' })}
          onClick={onClose}
          {...dragHandlers}
        />
        {body}
      </div>
    </div>
  )
}

function renderSection(
  section: RouteDetailsSection,
  index: number,
  onExpand: (legIndex: number) => void,
  onUpsellTap: () => void,
  strings: RouteDetailsStringSet,
): ReactElement {
  switch (section.kind) {
    case 'validation':
      return <ValidationRow key={section.id} type={section.validationType} strings={strings} />
    case 'entry':
      return (
        <EntryRow
          key={section.id}
          type={section.entryType}
          totalTime={section.totalTime}
          strings={strings}
        />
      )
    case 'firstStop':
      return (
        <FirstStopRow
          key={section.id}
          sectionIndex={section.sectionIndex}
          title={section.title}
          subtitle={section.subtitle}
          totalTime={section.totalTime}
          arrivalTime={section.arrivalTime}
          hexColor={section.hexColor}
          isOutlined={section.isOutlined}
          isSingleStop={section.isSingleStop}
          platformBound={section.platformBound}
          towards={section.towards}
          extra={section.extra}
          onUpsellTap={onUpsellTap}
          strings={strings}
        />
      )
    case 'expand':
      return (
        <ExpandRow
          key={section.id}
          sectionIndex={section.sectionIndex}
          title={section.title}
          isExpanded={section.isExpanded}
          hexColor={section.hexColor}
          isOutlined={section.isOutlined}
          onToggle={() => onExpand(section.sectionIndex)}
        />
      )
    case 'middleStop':
      return (
        <MiddleStopRow
          key={section.id}
          sectionIndex={section.sectionIndex}
          rowIndex={section.rowIndex}
          title={section.title}
          arrivalTime={section.arrivalTime}
          hexColor={section.hexColor}
          isOutlined={section.isOutlined}
        />
      )
    case 'lastStop':
      return (
        <LastStopRow
          key={section.id}
          sectionIndex={section.sectionIndex}
          title={section.title}
          arrivalTime={section.arrivalTime}
          hexColor={section.hexColor}
          isOutlined={section.isOutlined}
        />
      )
    case 'transfer':
      return (
        <TransferRow
          key={section.id}
          sectionIndex={section.sectionIndex}
          title={section.title}
          totalTime={section.totalTime}
        />
      )
    case 'streetWalk':
      return (
        <StreetWalkRow
          key={section.id}
          sectionIndex={section.sectionIndex}
          stationName={section.stationName}
          lineLabel={section.lineLabel}
          totalTime={section.totalTime}
          strings={strings}
        />
      )
    case 'streetEndpoint':
      return (
        <StreetEndpointRow
          key={section.id}
          sectionIndex={section.sectionIndex}
          role={section.role}
          stationName={section.stationName}
          leadsList={index === 0}
        />
      )
    case 'map':
      return (
        <MapRow
          key={section.id}
          stationName={section.stationName}
          originCoordinate={section.originCoordinate}
          destinationCoordinate={section.destinationCoordinate}
          strings={strings}
        />
      )
  }
}
